import React, { useEffect, useState } from 'react';
import MeddiDiabetesHealthPlan from './MeddiDiabetesHealthPlan';
import DiabetesHealthData from '../../../Modules/HealthData/Components/DiabetesHealthData';
import { useHealthPlanContext } from '../../../Services/HealthPlanContext';
import { useAppContext } from '../../../Services/AppContext';
import NavigationStepper, {
	NavigationStepperComponentParams,
	NavigationStepperItemType,
} from '../../Stepper/NavigationStepper';
import {
	HealthPlanCodeEnum,
	HealthPlanUser,
	useLazyHealthPlanUsersQuery,
	useRequestPaymentHealthPlanUserMutation,
} from '../../../graphql/graphql';
import useApiClient from '../../../Modules/Base/Hooks/useApiClient';
import useNotification from '../../../Hooks/useNotification.hook';
import useTranslation from '../../../Hooks/useTranslation';
import HealthPlanUserInitQuestionnaire from '../../../Modules/HealthPlan/Components/HealthPlanUserInitQuestionnaire';
import SubTabNavigationStepperItems from '../../Stepper/SubTabNavigationStepper/SubTabNavigationStepperItems';
import { useHealthPlanUserHook } from '../../../Modules/HealthPlan/Hooks/useHealthPlanUser';
import { useEffectAsync } from '../../../helpers';
import { getFormatedDate } from '../../../Helpers/date.helper';

interface MeddiDiabetesProps {
	workplaceHealthPlanId: string;
	patientId: string;
	setIsLoading: (isLoading: boolean) => void;
}

const MeddiDiabetes: React.FC<MeddiDiabetesProps> = ({
	workplaceHealthPlanId,
	patientId,
	setIsLoading,
}) => {
	const {
		userData: { id: userId },
		dateFormatMoment,
	} = useAppContext();
	const { healthPlanUser, setHealthPlanUser } = useHealthPlanContext();
	const [requestPaymentHealthPlanUser] =
		useRequestPaymentHealthPlanUserMutation();
	const { displayError } = useApiClient();
	const { showNotificationSuccess } = useNotification();
	const { t } = useTranslation();
	const [getHealthPlanUsers] = useLazyHealthPlanUsersQuery();
	const [isLoadingHealthPlanUsers, setIsLoadingHealthPlanUsers] =
		useState(true);
	const [healthPlans, setHealthPlans] = useState<HealthPlanUser[]>([]);
	const { initNewHealthPlanUser } = useHealthPlanUserHook();
	const [steps, setSteps] = useState<NavigationStepperItemType[]>([]);

	//LOAD HEALTH PLAN USERS
	const handleLoadHealthPlanUsers = async () => {
		setIsLoadingHealthPlanUsers(true);
		const result = await getHealthPlanUsers({
			userId: patientId,
			healthPlanCode: HealthPlanCodeEnum.Diabetes,
		});
		if (result) {
			const data = result?.data?.healthPlanUsers?.data;
			setHealthPlans(data as HealthPlanUser[]);
		}
		setIsLoadingHealthPlanUsers(false);
	};

	//FUNCTION FOR CREATING NEW HEALTH PLAN USER
	const handleInitNewHealthPlanUser = async () => {
		setIsLoading(true);
		const result = await initNewHealthPlanUser({
			healthPlanId: workplaceHealthPlanId,
			patientId,
		});
		if (result?.data?.createHealthPlanUser?.id) {
			handleLoadHealthPlanUsers();
		}
		setIsLoading(false);
	};

	//LOAD HEALTH PLAN ON COMPONENT INIT
	useEffectAsync(async () => {
		await handleLoadHealthPlanUsers();
	}, []);

	//CHECK IF YOU HAVE ANY HEALTH PLAN USER, IF NOT THAN CREATE ONE
	useEffectAsync(async () => {
		//IF HEALTH PLANS ARE NOT LOADING NOW
		if (!isLoadingHealthPlanUsers) {
			if (healthPlans.length === 0) {
				await handleInitNewHealthPlanUser();
			} else {
				getSteps();
			}
		}
	}, [isLoadingHealthPlanUsers]);

	useEffect(() => {
		setIsLoading(isLoadingHealthPlanUsers);
	}, [isLoadingHealthPlanUsers]);

	//WHAT TO DO WHEN USER CHANGE TAB
	const onHealthPlanStepChange = (healthPlanUser: HealthPlanUser) => {
		setHealthPlanUser(healthPlanUser);
		getSteps();
	};

	//FIRST HEALTH PLAN AS INITIAL STEP
	let initialStepKey = healthPlans?.length > 0 ? healthPlans[0]?.id : undefined;

	//PREPARING TABS
	const getSteps = () => {
		let tempHealthPlanUser = healthPlanUser;
		setIsLoading(true);
		const temp: NavigationStepperItemType[] = [];
		let canCreateNew = true;

		// ADD TAB FOR EACH HEALTH PLAN USER AND CHECK IF USER CAN EXTEND HEALTH PLAN
		healthPlans.forEach((item, key) => {
			if (item) {
				let label = t('base.healthPlan') ?? item.id;
				if (item.isActive) {
					label += ` (${t('component.healthPlan.ending')} ${getFormatedDate({
						date: item.activeTo,
						format: dateFormatMoment,
					})})`;
					if (!tempHealthPlanUser) {
						tempHealthPlanUser = item as HealthPlanUser;
						setHealthPlanUser(tempHealthPlanUser);
					}
				} else {
					canCreateNew = false;
				}
				temp.push({
					name: label,
					key: item.id,
					component: () => (
						<MeddiDiabetesHealthPlan
							patientId={patientId}
							healthPlanUser={item as HealthPlanUser}
							reFetchHealthPlanUser={handleLoadHealthPlanUsers}
						/>
					),
					onStepChange: () => onHealthPlanStepChange(item as HealthPlanUser),
				});
			}
		});

		//REGULAR TABS, WHEN YOU HAVE AT LEAST ONE ACTIVE HEALTH PLAN USER
		if (healthPlans.some(item => item.ready)) {
			temp.push({
				name: 'base.monitoring',
				key: 'healthData',
				tooltip: 'base.dataJustForDiabetes',
				component: () => <DiabetesHealthData patientId={patientId} />,
			});

			// if (!healthPlanUser?.isActive) {
			// 	temp.push({
			// 		name: 'component.meddiDiabetes.requestPayment',
			// 		key: 'requestPayment',
			// 		onClick: () => handleRequestPayment(),
			// 	});
			// }

			temp.push({
				name: 'base.createdHealthPlan',
				key: 'reviseEntryQuestionnaire',
				component: (params: NavigationStepperComponentParams) => (
					<HealthPlanUserInitQuestionnaire
						patientId={patientId}
						readOnly
						setIsLoading={params.setIsLoading}
					/>
				),
			});
		}

		//ADDING BUTTON FOR EXTENDING HEALTH PLAN
		if (canCreateNew) {
			temp.push({
				name: 'base.extendHealthPlan',
				key: 'extendHealthPlan',
				onClick: () => handleInitNewHealthPlanUser(),
			});
		}

		setSteps(temp);
		setIsLoading(false);
	};

	//FUNCTION FOR REQUESTING PAYMENT OF HEALTH PLAN
	const handleRequestPayment = async () => {
		setIsLoading(true);
		try {
			if (healthPlanUser?.id) {
				await requestPaymentHealthPlanUser({
					input: {
						healthPlanUserId: healthPlanUser.id,
					},
				}).unwrap();
				showNotificationSuccess({
					content: t('component.medicalReport.sentToPatient'),
				});
			}
		} catch (e) {
			displayError(e);
		}
		setIsLoading(false);
	};

	return (
		<NavigationStepper
			steps={steps}
			initialStepPath={initialStepKey}
			canSkip
			renderItems={({ items, activeKey, onChange }) => (
				<SubTabNavigationStepperItems
					items={items}
					activeKey={activeKey}
					onChange={onChange}
					firstUpper={true}
				/>
			)}
		/>
	);
};

export default MeddiDiabetes;
